/**
 * Canonical role slugs. The six SYSTEM roles the fleet is converging on
 * (admin / team_leader / coordinator / accountant / doctor / agent — see
 * docs/ROLE-CONSOLIDATION-260725.md) plus the two names the DATA still uses and
 * which therefore cannot be dropped yet:
 *
 *   FRONTDESK  the predecessor of `coordinator` (D2). Live on Dentex, Dentour,
 *              Gjergji, Zaffiro; the rename is a later stage.
 *   MANAGER    an optional per-clinic extra with diverged meaning (D3) — real
 *              production data on Smile (→ accountant) and Dentour
 *              (→ team_leader).
 *
 * Backend Spatie role names match these slugs.
 */
export const ROLES = {
  ADMIN: "admin",
  TEAM_LEADER: "team_leader",
  COORDINATOR: "coordinator",
  ACCOUNTANT: "accountant",
  DOCTOR: "doctor",
  AGENT: "agent",
  FRONTDESK: "frontdesk",
  MANAGER: "manager",
  /**
   * An OUTSIDE referral partner, not clinic staff. Their whole app is
   * /affiliator/patients plus the form behind it; every other surface gates
   * itself out because the role holds none of its permissions.
   */
  AFFILIATOR: "affiliator",
  /**
   * The clinic's photographer (ArtDent 260903). Attaches before/after pictures
   * to a patient and does nothing else — the patient page collapses to one
   * rail for them, and both patient lists are theirs only to pick from.
   *
   * NOTE the UI gates on PERMISSIONS, not on this slug (`leads.photos.upload`
   * without `leads.notes.view`), so a clinic that builds its own photographer
   * role inherits the posture. This constant is for labels and filters.
   */
  MARKETING: "marketing",
  /**
   * Files a patient's imaging and nothing else (ArtDent 260914). The
   * photographer's shape with two folders swapped: 2D and 3D x-ray instead of
   * before/after, reached from the Clients list alone.
   *
   * Same note as MARKETING — the UI gates on PERMISSIONS (`leads.xrays.upload`
   * without `leads.notes.view`), not on this slug, except where a nav ALLOWLIST
   * has to be keyed on the role; see app-sidebar.tsx for why.
   */
  RADIOGRAPHER: "radiographer",
  /**
   * The person who drives the patient (ArtDent 260903). Holds exactly one
   * permission, `logistics.drive`: they claim an open transfer run and report
   * what happened on it. Deliberately NOT a dispatcher — `logistics.view`
   * would hand a contracted driver the clinic-wide board.
   *
   * Like every role here, the UI gates on the PERMISSION, not this slug, so a
   * clinic that invents its own driver role inherits the same surfaces.
   */
  DRIVER: "driver",
  /**
   * The transport half of Radiance's split coordinator (261001). For now it
   * starts as the coordinator — its grants copied at creation, its dashboard
   * via STAND_INS in dashboard-role.ts — and is trimmed from there. Unlike
   * every role above it exists at Radiance ONLY (a `create` in its clinic
   * profile), so nothing may assume the role is seeded elsewhere.
   */
  LOGISTICS_COORDINATOR: "logistics_coordinator",
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

/**
 * Legacy → canonical slug aliases: `agent` was formerly `operator`,
 * `frontdesk` was formerly `receptionist`.
 *
 * These aliases are a safety net for live, in-flight SPA sessions ONLY. The
 * auth store persists just the token (stores/auth-store.ts) — `user.roles` is
 * refetched from `/me` on every reload — and frontend + backend always ship
 * together per clinic, so the only window where a session still carries a
 * legacy role name is between the deploy and the user's next page reload. Once
 * the fleet has cycled, these aliases can be removed.
 *
 * NOTE `frontdesk` is deliberately NOT aliased to `coordinator` yet. It is a
 * live role name in every clinic database, not an in-flight leftover, and gates
 * that check both slugs must be able to tell them apart until the data rename
 * lands. Add the alias in the same release as the rename migration.
 */
const ROLE_ALIASES: Record<string, string> = {
  operator: ROLES.AGENT,
  receptionist: ROLES.FRONTDESK,
}

/**
 * True when `roles` grants `role`, treating any legacy alias as its canonical
 * slug (so `hasRole(["operator"], ROLES.AGENT)` is true during the migration
 * window). Safe against an undefined/empty roles array.
 */
export function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || roles.length === 0) return false
  return roles.some((r) => r === role || ROLE_ALIASES[r] === role)
}

/**
 * The roles whose day is a SALES pipeline rather than a clinic roster — the
 * desktop sidebar's "Sales" group (agent, team_leader). They work `/leads`;
 * everyone else works `/clients` and `/kartele`.
 *
 * The mobile app collapses both into one screen backed by `getLeads`, so this
 * is what decides whether that screen calls its rows leads or patients, and
 * whether it is ordered newest-first (a pipeline) or A–Z (a lookup).
 *
 * Deliberately NOT `coordinator`: they hold leads.view.* too, but their desktop
 * home is the Clinic group and they look people up by name.
 */
export function isLeadPosture(roles: string[] | undefined): boolean {
  return hasRole(roles, ROLES.AGENT) || hasRole(roles, ROLES.TEAM_LEADER)
}
